/**
 * Render persisted message `content` parts (from `GET /conversations/:id`) into
 * the same {@link StreamItem} model the live reducer produces, so ONE renderer
 * draws both history and the in-flight run. Content parts are AI-SDK shapes
 * (`{ type: 'text', text }`, tool parts) plus inline directives / media refs;
 * this stays permissive — an unrecognized part is skipped, never a throw.
 */
import { extractDirective, extractMediaRef } from '../generative-ui/directive.ts';
import type { StreamItem } from './stream-model.ts';

/** Pull the plain text out of one content part, or `''` when it carries none. */
function partText(part: unknown): string {
  if (typeof part === 'string') {
    return part;
  }
  if (part && typeof part === 'object') {
    const record = part as Record<string, unknown>;
    if (record.type === 'text' && typeof record.text === 'string') {
      return record.text;
    }
  }
  return '';
}

/** The concatenated text of a message's content parts (the composer/user-turn view). */
export function messageText(parts: readonly unknown[]): string {
  return parts.map(partText).join('');
}

/**
 * Turn a persisted message's content parts into ordered {@link StreamItem}s:
 * contiguous text is coalesced into one text item, directives and media refs
 * become their own items in place. Ids are index-derived (stable per render).
 */
export function messageItems(parts: readonly unknown[]): StreamItem[] {
  const items: StreamItem[] = [];
  let seq = 0;
  const pushText = (text: string): void => {
    if (text === '') {
      return;
    }
    const last = items[items.length - 1];
    if (last && last.kind === 'text') {
      items[items.length - 1] = { ...last, text: last.text + text };
    } else {
      items.push({ kind: 'text', id: `c${seq}`, text });
      seq += 1;
    }
  };

  for (const part of parts) {
    const directive = extractDirective(part);
    if (directive) {
      items.push({ kind: 'directive', id: `c${seq}`, directive, answered: true });
      seq += 1;
      continue;
    }
    const media = extractMediaRef(part);
    if (media) {
      items.push({ kind: 'media', id: `c${seq}`, ref: media });
      seq += 1;
      continue;
    }
    pushText(partText(part));
  }
  return items;
}
